import { API_URL } from '../config';
import { getInvoiceSeller } from '../constants/invoiceSeller';
import { buildOrderInvoiceData } from './buildOrderInvoiceData';
import { generateOrderInvoicePdf } from './generateOrderInvoicePdf';
import { buildCheckoutAuthHeaders } from './checkoutSession';
import {
  hasConfiguredFiscalData,
  mapAcademyFiscalDataToInvoiceSeller,
  pickAcademyFiscalFields,
} from './mapAcademyFiscalDataToInvoiceSeller';
import { resolveOrderInvoiceBilling } from './orderInvoiceBilling';
import { resolveCheckoutBillingInitialValues } from './resolveCheckoutBillingInitialValues';

/**
 * Loads the academy fiscal data used as invoice issuer at generation time.
 * @returns {Promise<Record<string, string> | null>}
 */
export async function fetchAcademyFiscalDataForInvoice() {
  try {
    const response = await fetch(`${API_URL}/api/configuracion/datos-fiscales-academia`, {
      headers: buildCheckoutAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      return null;
    }
    const payload = await response.json();
    return pickAcademyFiscalFields(payload);
  } catch {
    return null;
  }
}

/**
 * @param {{ name?: string; email?: string } | null | undefined} billing
 * @returns {boolean}
 */
function hasInvoiceBuyerIdentity(billing) {
  const name = String(billing?.name || '').trim();
  const email = String(billing?.email || '').trim();
  return Boolean(name || email);
}

/**
 * @param {Partial<import('./orderInvoiceBilling').OrderInvoiceBilling> | null | undefined} source
 * @returns {import('./orderInvoiceBilling').OrderInvoiceBilling}
 */
function toInvoiceBilling(source) {
  return {
    name: String(source?.name || '').trim(),
    email: String(source?.email || '').trim(),
    phone: String(source?.phone || '').trim(),
    address: String(source?.address || '').trim(),
    city: String(source?.city || '').trim(),
    postalCode: String(source?.postalCode || '').trim(),
    country: String(source?.country || '').trim(),
  };
}

/**
 * @param {{
 *   order: Record<string, string | number | boolean | Array<Record<string, string | number | boolean>> | undefined> | null | undefined;
 *   orderId: string | null | undefined;
 *   billing?: import('./orderInvoiceBilling').OrderInvoiceBilling | null;
 *   seller?: import('../constants/invoiceSeller').InvoiceSeller | null;
 * }} params
 */
export async function downloadOrderInvoice({ order, orderId, billing = null, seller = null }) {
  let resolvedBilling = resolveOrderInvoiceBilling(orderId, order, billing);
  if (!hasInvoiceBuyerIdentity(resolvedBilling)) {
    const profileBilling = await resolveCheckoutBillingInitialValues(orderId);
    if (hasInvoiceBuyerIdentity(profileBilling)) {
      resolvedBilling = toInvoiceBilling(profileBilling);
    }
  }
  if (!hasInvoiceBuyerIdentity(resolvedBilling)) {
    throw new Error('No hay datos de facturación disponibles para generar la factura.');
  }

  const fiscalFromOrder = hasConfiguredFiscalData(order)
    ? pickAcademyFiscalFields(order)
    : null;
  const resolvedSeller = seller || mapAcademyFiscalDataToInvoiceSeller(
    fiscalFromOrder || await fetchAcademyFiscalDataForInvoice(),
    getInvoiceSeller(),
  );

  const invoiceData = buildOrderInvoiceData({
    order,
    orderId,
    billing: resolvedBilling,
    seller: resolvedSeller,
  });

  generateOrderInvoicePdf(invoiceData);
}
